import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Buildings, Users, Eye, Pause, Play, Calendar, Trash, Crown, MagnifyingGlass, ArrowsClockwise, X, ClockCounterClockwise, Receipt, Phone } from '@phosphor-icons/react';

/**
 * Super-Admin Console (alleen voor BillFac platform-eigenaar).
 * Toont alle tenants met KPI's + acties (schorsen/activeren/trial verlengen/verwijderen/lifetime).
 */
export default function SuperAdminPage() {
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusF, setStatusF] = useState('');
  const [q, setQ] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [detailTenantId, setDetailTenantId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [findEmail, setFindEmail] = useState('');
  const [findResults, setFindResults] = useState(null);
  const [findLoading, setFindLoading] = useState(false);
  const [charts, setCharts] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [s, t, c] = await Promise.all([
        API.get('/super-admin/stats'),
        API.get('/super-admin/tenants', { params: { status: statusF || undefined, q: q || undefined } }),
        API.get('/super-admin/charts').catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setTenants(t.data.tenants || []);
      setCharts(c.data);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Alleen platform-eigenaar (super-admin) heeft toegang.' : (e.response?.data?.detail || 'Kon data niet laden'));
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF]);

  // Detail-modal data ophalen
  useEffect(() => {
    if (!detailTenantId) { setDetail(null); return; }
    setDetailLoading(true);
    API.get(`/super-admin/tenants/${detailTenantId}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [detailTenantId]);

  // Audit log ophalen wanneer modal opent
  useEffect(() => {
    if (!showAuditLog) return;
    API.get('/super-admin/audit-log', { params: { limiet: 100 } })
      .then(r => setAuditLogs(r.data.logs || []))
      .catch(() => setAuditLogs([]));
  }, [showAuditLog]);

  const findUserByEmail = async (overrideTerm = null) => {
    const term = (overrideTerm !== null ? overrideTerm : findEmail).trim();
    if (term !== 'ALL' && term.length < 3) {
      alert('Vul minstens 3 tekens van het e-mailadres in (of "ALL" om alle users te tonen)');
      return;
    }
    setFindLoading(true);
    try {
      const r = await API.get('/super-admin/find-user', { params: { email: term } });
      setFindResults(r.data.resultaten || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Zoeken mislukt');
      setFindResults([]);
    }
    setFindLoading(false);
  };

  const doVerkoperDelete = async (verkoperId, email) => {
    if (!window.confirm(`Verwijder verkoper "${email}" permanent?\n\nDit verwijdert ENKEL het verkoper-account (sub-user), niet het hoofdbedrijf.`)) return;
    setActionLoading(p => ({ ...p, [verkoperId]: 'verkoper_delete' }));
    try {
      const r = await API.delete(`/super-admin/verkoper/${verkoperId}`);
      alert(`✓ Verkoper verwijderd: ${r.data.email || verkoperId}`);
      // Herzoek
      if (findEmail) await findUserByEmail();
    } catch (e) {
      alert(e.response?.data?.detail || 'Verwijderen mislukt');
    }
    setActionLoading(p => ({ ...p, [verkoperId]: null }));
  };

  const doActie = async (tenantId, actie, extraDagen = null) => {
    const labels = {
      suspend: 'schorsen',
      activate: 'activeren',
      verleng_trial: `trial verlengen met ${extraDagen} dagen`,
      set_lifetime: 'op LIFETIME zetten (gratis voor altijd)',
      delete: 'permanent verwijderen',
      maak_super_admin: 'SUPER-ADMIN rechten geven (volledige platform-toegang)',
      verwijder_super_admin: 'super-admin rechten intrekken',
    };
    if (!window.confirm(`Weet je zeker dat je deze tenant wil ${labels[actie] || actie}?`)) return;
    setActionLoading(p => ({ ...p, [tenantId]: actie }));
    try {
      await API.post(`/super-admin/tenants/${tenantId}/actie`, { actie, extra_dagen: extraDagen });
      await load();
      if (findResults && findEmail) await findUserByEmail();
      if (detailTenantId === tenantId) API.get(`/super-admin/tenants/${tenantId}`).then(r => setDetail(r.data)).catch(() => {});
    } catch (e) {
      alert(e.response?.data?.detail || 'Actie mislukt');
    }
    setActionLoading(p => ({ ...p, [tenantId]: null }));
  };

  const doHardDelete = async (tenantId, email) => {
    const c1 = window.confirm(
      `⚠️ HARD DELETE — PERMANENT EN ONOMKEERBAAR!\n\n` +
      `Dit verwijdert PERMANENT:\n` +
      `  • Het user-account (${email || tenantId})\n` +
      `  • Het tenant-record\n` +
      `  • ALLE documenten, klanten, artikels, leveranciers, verkopers, ...\n\n` +
      `Geen herstel mogelijk. Doorgaan?`
    );
    if (!c1) return;
    const c2 = window.prompt(`Typ DELETE (in hoofdletters) om te bevestigen:`);
    if (c2 !== 'DELETE') {
      alert('Geannuleerd — bevestiging incorrect.');
      return;
    }
    setActionLoading(p => ({ ...p, [tenantId]: 'hard_delete' }));
    try {
      const r = await API.delete(`/super-admin/tenants/${tenantId}/hard-delete?bevestiging=DELETE`);
      const purged = Object.entries(r.data.purge_counts || {}).map(([k, v]) => `  ${k}: ${v}`).join('\n') || '  (geen extra data)';
      alert(`✓ Permanent verwijderd: ${r.data.user_email || tenantId}\n\nGepurged:\n${purged}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Hard delete mislukt');
    }
    setActionLoading(p => ({ ...p, [tenantId]: null }));
  };

  const doWipeAuditLog = async (tenantId, ownerEmail) => {
    const c1 = window.confirm(
      `🗑️ Activiteitenlog wissen voor tenant:\n\n${ownerEmail}\n\n` +
      `Alle audit-log entries (CRUD-acties van deze tenant) worden permanent verwijderd.\n` +
      `Gebruikt voor: GDPR right-to-be-forgotten, test-account opruiming, of incident remediation.\n\n` +
      `De tenant zelf, klanten, documenten en andere data blijven onaangetast.\n\n` +
      `Doorgaan?`
    );
    if (!c1) return;
    setActionLoading(p => ({ ...p, [tenantId]: 'wipe_audit' }));
    try {
      const r = await API.delete(`/super-admin/tenants/${tenantId}/audit-log`);
      const msg = r.data.deleted === 0
        ? `Activiteitenlog was al leeg voor ${r.data.tenant_email || tenantId}.`
        : `✓ ${r.data.deleted} entries gewist voor ${r.data.tenant_email || tenantId}.`;
      alert(msg);
    } catch (e) {
      alert(e.response?.data?.detail || 'Wis-actie mislukt');
    }
    setActionLoading(p => ({ ...p, [tenantId]: null }));
  };

  const statusBadge = (s) => {
    const map = {
      active: { bg: '#10B981', label: '✓ Actief' },
      trial: { bg: '#3B82F6', label: '🎁 Trial' },
      suspended: { bg: '#F59E0B', label: '⏸ Geschorst' },
      deleted: { bg: '#EF4444', label: '🗑 Verwijderd' },
      orphan: { bg: '#F97316', label: '⚠️ Geen tenant' },
    };
    const m = map[s] || { bg: '#6B7280', label: s };
    return <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded" style={{ background: m.bg + '20', color: m.bg, border: `1px solid ${m.bg}40` }}>{m.label}</span>;
  };

  const planBadge = (p) => {
    const map = {
      lifetime: { c: '#7C3AED', label: '♾ Lifetime', icon: '👑' },
      trial: { c: '#3B82F6', label: 'Trial' },
      starter: { c: '#10B981', label: 'Starter' },
      pro: { c: '#F59E0B', label: 'Pro' },
      enterprise: { c: '#DC2626', label: 'Enterprise' },
    };
    const m = map[p] || { c: '#6B7280', label: p };
    return <span className="text-[10px] font-bold" style={{ color: m.c }}>{m.icon || ''} {m.label}</span>;
  };

  if (error) return (
    <div className="p-8" data-testid="super-admin-error">
      <div className="bg-red-50 border border-red-200 p-6 rounded text-center">
        <Crown size={32} className="mx-auto mb-2 text-red-500" />
        <div className="text-red-700 font-bold mb-1">Toegang geweigerd</div>
        <div className="text-red-600 text-sm">{error}</div>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="super-admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2">
            <Crown size={26} className="text-[#7C3AED]" /> Super-Admin Console
          </h1>
          <p className="text-xs text-gray-500 mt-1">BillFac platform-beheer — alle tenants, statussen en acties</p>
        </div>
        <button onClick={load} className="btn btn-secondary btn-sm" disabled={loading} data-testid="super-admin-refresh">
          <ArrowsClockwise size={14} /> Vernieuwen
        </button>
        <button
          onClick={async () => {
            if (!window.confirm('Maak tenant-records aan voor alle users zonder tenant?\n\nBestaande users (>1 dag oud) krijgen automatisch "active + lifetime".\nNieuwe registraties krijgen "trial" (14 dagen).')) return;
            try {
              const r = await API.post('/super-admin/sync-tenants');
              alert(`✓ Sync klaar: ${r.data.aangemaakt} tenant-records aangemaakt (van ${r.data.totaal_users} users)`);
              load();
            } catch (e) {
              alert(e.response?.data?.detail || 'Sync mislukt');
            }
          }}
          className="btn btn-primary btn-sm ml-2"
          data-testid="super-admin-sync-tenants"
          title="Maak tenant-records aan voor alle users die er nog geen hebben (orphans)"
        >
          🔄 Sync orphans
        </button>
        <button
          onClick={() => setShowAuditLog(true)}
          className="btn btn-secondary btn-sm ml-2"
          data-testid="super-admin-audit-log-btn"
          title="Bekijk alle super-admin acties (audit-trail)"
        >
          <ClockCounterClockwise size={14} /> Audit-log
        </button>
        <button
          onClick={async () => {
            if (!window.confirm('🔒 Data-leak scan — controleer ALLE GET-endpoints op missing tenant-filter.\n\nDit duurt 1-2 seconden. Geen wijzigingen, alleen lezen.')) return;
            try {
              const r = await API.post('/super-admin/data-leak-scan');
              const d = r.data || {};
              const hits = (d.details || []);
              if (hits.length === 0) {
                alert(`✅ Geen data-leak risico's gevonden!\n\n${d.totaal_endpoints_gescand} GET-endpoints gescand, 0 verdachte hits.\n\nAlle endpoints zijn goed tenant-gescoped.`);
                return;
              }
              const sample = hits.slice(0, 30).map(h => `• ${h.route}  (regel ${h.regel}, collectie=${h.collectie})`).join('\n');
              alert(
                `⚠️ ${hits.length} VERDACHTE ENDPOINTS\n\n` +
                `${d.totaal_endpoints_gescand} GET-endpoints gescand. ${hits.length} hebben mogelijk geen tenant-filter.\n\n` +
                `Top ${Math.min(30, hits.length)} hits:\n${sample}\n\n` +
                (hits.length > 30 ? `... en nog ${hits.length - 30} meer (zie console.log)\n\n` : '') +
                `Review elk hit manueel: open server.py op de aangegeven regel en check of de query een user_id/parent_user_id/tenant_id filter heeft.`
              );
              console.log('🔒 Data-leak scan — verdachte endpoints:', d);
            } catch (e) {
              alert('Scan mislukt: ' + (e.response?.data?.detail || e.message));
            }
          }}
          className="btn btn-secondary btn-sm ml-2"
          data-testid="super-admin-data-leak-scan-btn"
          title="Statisch scan server.py op endpoints zonder tenant-filter (data-leak preventie)"
        >
          🔒 Data-leak scan
        </button>
      </div>

      {/* KPI's */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6" data-testid="super-admin-stats">
          {[
            { k: 'totaal_tenants', label: 'Totaal', color: '#002FA7', icon: Buildings },
            { k: 'actief', label: 'Actief', color: '#10B981', icon: Play },
            { k: 'in_trial', label: 'In Trial', color: '#3B82F6', icon: Calendar },
            { k: 'suspended', label: 'Geschorst', color: '#F59E0B', icon: Pause },
            { k: 'deleted', label: 'Verwijderd', color: '#EF4444', icon: Trash },
            { k: 'orphans', label: 'Orphans ⚠️', color: '#F97316', icon: Users },
            { k: 'nieuw_30d', label: 'Nieuw (30d)', color: '#7C3AED', icon: Users },
          ].map(s => {
            const Icon = s.icon;
            return (
              <div key={s.k} className="bg-white border border-gray-200 p-3 rounded shadow-sm" data-testid={`stat-${s.k}`}>
                <div className="flex items-center justify-between mb-1">
                  <Icon size={16} style={{ color: s.color }} />
                  <span className="text-[10px] uppercase font-bold text-gray-400">{s.label}</span>
                </div>
                <div className="text-2xl font-extrabold" style={{ color: s.color }}>{stats[s.k] || 0}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grafieken: MRR, signups, plan distributie, conversion */}
      {charts && <SuperAdminCharts charts={charts} />}

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-3 rounded shadow-sm mb-4 flex items-center gap-3 flex-wrap" data-testid="super-admin-filters">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Zoek op naam of e-mail..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="field-input pl-8"
            data-testid="super-admin-search"
          />
        </div>
        <select
          value={statusF}
          onChange={(e) => setStatusF(e.target.value)}
          className="field-input max-w-[150px]"
          data-testid="super-admin-status-filter"
        >
          <option value="">Alle statussen</option>
          <option value="active">Actief</option>
          <option value="trial">Trial</option>
          <option value="suspended">Geschorst</option>
          <option value="deleted">Verwijderd</option>
        </select>
        <button onClick={load} className="btn btn-primary btn-sm">Zoeken</button>
      </div>

      {/* 🔎 Diepe user-lookup — vind ELK account in de DB, ook als het niet in de tenants-lijst staat */}
      <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-4" data-testid="super-admin-find-user">
        <div className="text-xs font-bold text-orange-800 mb-2">🔎 Diepe zoekfunctie — vind ELK account in de database</div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={findEmail}
            onChange={(e) => setFindEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && findUserByEmail()}
            placeholder="bv. info@gontex.be of een deel ervan"
            className="field-input flex-1 max-w-md"
            data-testid="find-user-email-input"
          />
          <button onClick={findUserByEmail} disabled={findLoading} className="btn btn-primary btn-sm" data-testid="find-user-btn">
            {findLoading ? '⏳ Zoeken...' : 'Zoek user'}
          </button>
          <button
            onClick={() => { setFindEmail('ALL'); findUserByEmail('ALL'); }}
            disabled={findLoading}
            className="btn btn-secondary btn-sm"
            title="Toon ALLE users in de database, ongeacht filter — handig om verloren accounts te vinden"
            data-testid="find-all-users-btn"
          >Toon ALLE users</button>
          {findResults && findResults.length > 0 && (
            <button onClick={() => { setFindResults(null); setFindEmail(''); }} className="text-xs text-gray-500 hover:text-gray-700">✕ Wis</button>
          )}
        </div>
        {findResults !== null && (
          <div className="mt-3 bg-white border border-orange-200 rounded p-2">
            <div className="text-[10px] uppercase font-bold text-gray-500 mb-2">{findResults.length} resultaat/resultaten</div>
            {findResults.length === 0 && <div className="text-xs text-gray-500 py-2 text-center">Geen accounts gevonden met dit e-mailadres.</div>}
            {findResults.map((r, i) => (
              <div key={i} className="border-t border-gray-100 py-2 first:border-t-0 text-xs flex items-center justify-between gap-2" data-testid={`find-result-${r.verkoper_id || r.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 flex items-center gap-1.5">
                    {r.email}
                    {r.is_super_admin && <span className="text-purple-600">👑</span>}
                    {r.account_type === 'verkoper' && (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[9px] font-bold">VERKOPER (sub-user)</span>
                    )}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5 truncate">
                    {r.account_type === 'verkoper' ? (
                      <>verkoper-id: <span className="font-mono">{r.verkoper_id}</span> · {r.name || '—'} · {r.user_created_at?.slice(0, 10)}</>
                    ) : (
                      <><span className="font-mono">{r.user_id}</span> · {r.name || '—'} · rol: {r.role || '—'} · {r.user_created_at?.slice(0, 10)}</>
                    )}
                  </div>
                  <div className="text-[10px] mt-1">
                    {r.account_type === 'verkoper' ? (
                      <span className="inline-block px-1.5 py-0.5 rounded font-bold" style={{background: '#dbeafe', color: '#1e40af'}}>
                        👤 Sub-user onder bedrijf: <strong>{r.tenant_naam || '—'}</strong> ({r.parent_email || '—'})
                      </span>
                    ) : r.heeft_tenant ? (
                      <span className="inline-block px-1.5 py-0.5 rounded font-bold" style={{background: '#dcfce7', color: '#166534'}}>
                        ✓ Heeft tenant — status: <strong>{r.tenant_status}</strong> · plan: <strong>{r.tenant_plan}</strong>
                      </span>
                    ) : (
                      <span className="inline-block px-1.5 py-0.5 rounded font-bold" style={{background: '#fef3c7', color: '#92400e'}}>
                        ⚠️ ORPHAN — geen tenant-record
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {r.account_type === 'verkoper' ? (
                    <button
                      onClick={() => doVerkoperDelete(r.verkoper_id, r.email)}
                      className="px-2 py-1 bg-red-700 hover:bg-red-800 text-white rounded text-[10px] font-bold"
                      title="Verwijder ENKEL deze verkoper (sub-user)"
                      data-testid={`find-verkoper-delete-${r.verkoper_id}`}
                    >🗑 Verwijder verkoper</button>
                  ) : (
                    <>
                      {!r.is_super_admin ? (
                        <button
                          onClick={() => doActie(r.user_id, 'maak_super_admin')}
                          disabled={!!actionLoading[r.user_id]}
                          className="px-2 py-1 bg-purple-700 hover:bg-purple-800 text-white rounded text-[10px] font-bold disabled:opacity-50"
                          title="Geef deze gebruiker volledige Super-Admin toegang tot het platform"
                          data-testid={`find-maak-super-admin-${r.user_id}`}
                        >👑 Maak super-admin</button>
                      ) : (
                        <button
                          onClick={() => doActie(r.user_id, 'verwijder_super_admin')}
                          disabled={!!actionLoading[r.user_id]}
                          className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-[10px] font-bold disabled:opacity-50"
                          title="Trek super-admin rechten van deze gebruiker in"
                          data-testid={`find-verwijder-super-admin-${r.user_id}`}
                        >Verwijder super-admin</button>
                      )}
                      {!r.is_super_admin && (
                        <button
                          onClick={() => doHardDelete(r.user_id, r.email)}
                          className="px-2 py-1 bg-red-700 hover:bg-red-800 text-white rounded text-[10px] font-bold"
                          title="⚠️ HARD DELETE — verwijdert user + tenant + ALLE data permanent"
                          data-testid={`find-hard-delete-${r.user_id}`}
                        >💥 Hard Delete</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tenant-tabel */}
      <div className="bg-white border border-gray-200 rounded shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="super-admin-tenants-table">
          <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Bedrijf</th>
              <th className="text-left px-3 py-2">Eigenaar e-mail</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-center px-3 py-2">Plan</th>
              <th className="text-center px-3 py-2">Trial Rest</th>
              <th className="text-right px-3 py-2">Docs</th>
              <th className="text-right px-3 py-2">Klanten</th>
              <th className="text-right px-3 py-2">Users</th>
              <th className="text-left px-3 py-2">Aangemaakt</th>
              <th className="text-center px-3 py-2">Acties</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center text-gray-400 py-6">Laden...</td></tr>}
            {!loading && tenants.length === 0 && <tr><td colSpan={10} className="text-center text-gray-400 py-6">Geen tenants gevonden.</td></tr>}
            {!loading && tenants.map(t => (
              <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`tenant-row-${t.id}`}>
                <td className="px-3 py-2 font-bold">
                  <button
                    onClick={() => setDetailTenantId(t.id)}
                    className="text-[#002FA7] hover:underline text-left"
                    data-testid={`view-${t.id}`}
                  >
                    {t.naam || '—'}
                  </button>
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-gray-600">{t.owner_email || '—'}</td>
                <td className="text-center px-3 py-2">{statusBadge(t.status)}</td>
                <td className="text-center px-3 py-2">{planBadge(t.plan_tier)}</td>
                <td className="text-center px-3 py-2">
                  {t.status === 'trial' && t.trial_dagen_over !== null
                    ? <span className={`font-bold ${t.trial_dagen_over <= 3 ? 'text-red-600' : 'text-blue-600'}`}>{t.trial_dagen_over}d</span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="text-right px-3 py-2 font-mono">{t.aantal_documenten || 0}</td>
                <td className="text-right px-3 py-2 font-mono">{t.aantal_klanten || 0}</td>
                <td className="text-right px-3 py-2 font-mono">{t.aantal_users || 0}</td>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                  {t.created_at ? new Date(t.created_at).toLocaleDateString('nl-BE') : '—'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-center flex-wrap">
                    {t.status !== 'active' && (
                      <button onClick={() => doActie(t.id, 'activate')} className="px-1.5 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded text-[10px] font-bold" title="Activeer (geen trial-deadline meer)" disabled={!!actionLoading[t.id]} data-testid={`activate-${t.id}`}>▶</button>
                    )}
                    {t.status === 'active' || t.status === 'trial' ? (
                      <button onClick={() => doActie(t.id, 'suspend')} className="px-1.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded text-[10px] font-bold" title="Schors (gebruiker kan niet meer inloggen)" disabled={!!actionLoading[t.id]} data-testid={`suspend-${t.id}`}>⏸</button>
                    ) : null}
                    {(t.status === 'trial' || t.status === 'suspended') && (
                      <button onClick={() => doActie(t.id, 'verleng_trial', 30)} className="px-1.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-[10px] font-bold" title="Verleng trial met 30 dagen" disabled={!!actionLoading[t.id]} data-testid={`extend-${t.id}`}>+30d</button>
                    )}
                    {t.plan_tier !== 'lifetime' && (
                      <button onClick={() => doActie(t.id, 'set_lifetime')} className="px-1.5 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-[10px] font-bold" title="Lifetime — gratis voor altijd" disabled={!!actionLoading[t.id]} data-testid={`lifetime-${t.id}`}>♾</button>
                    )}
                    {t.status !== 'deleted' && (
                      <button onClick={() => doActie(t.id, 'delete')} className="px-1.5 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-[10px] font-bold" title="Soft delete — markeert als verwijderd, herstelbaar via ▶" disabled={!!actionLoading[t.id]} data-testid={`delete-${t.id}`}>🗑</button>
                    )}
                    <button onClick={() => doWipeAuditLog(t.id, t.owner_email)} className="px-1.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded text-[10px] font-bold" title="🗑️ Wis Activiteitenlog van deze tenant (GDPR right-to-be-forgotten / cleanup)" disabled={!!actionLoading[t.id]} data-testid={`wipe-audit-${t.id}`}>📋🗑</button>
                    <button onClick={() => doHardDelete(t.id, t.owner_email)} className="px-1.5 py-1 bg-red-700 hover:bg-red-800 text-white rounded text-[10px] font-bold" title="⚠️ HARD DELETE — verwijdert tenant + user + ALLE data permanent (onomkeerbaar)" disabled={!!actionLoading[t.id]} data-testid={`hard-delete-${t.id}`}>💥</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-[11px] text-gray-400 flex items-center gap-1.5">
        <Eye size={12} /> Tip: alle acties worden geaudit-logged in <code className="bg-gray-100 px-1 font-mono">super_admin_audit</code> collectie.
      </div>

      {/* ─── Tenant Detail Modal ─── */}
      {detailTenantId && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" onClick={() => setDetailTenantId(null)} data-testid="tenant-detail-modal">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
              <h2 className="font-bold text-lg flex items-center gap-2"><Buildings size={20} /> Tenant details</h2>
              <button onClick={() => setDetailTenantId(null)} className="p-1 hover:bg-gray-100 rounded" data-testid="close-detail"><X size={18} /></button>
            </div>
            <div className="p-5">
              {detailLoading && <div className="text-center text-gray-400 py-8">Laden...</div>}
              {!detailLoading && detail && (
                <>
                  {/* Tenant + owner cards */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <div className="text-[10px] uppercase font-bold text-blue-600 mb-1">Tenant</div>
                      <div className="font-bold text-base">{detail.tenant.naam}</div>
                      <div className="text-xs text-gray-600 mt-1">{statusBadge(detail.tenant.status)} {planBadge(detail.tenant.plan_tier)}</div>
                      <div className="text-[11px] text-gray-500 mt-2">Aangemaakt: {detail.tenant.created_at ? new Date(detail.tenant.created_at).toLocaleDateString('nl-BE') : '—'}</div>
                      {detail.tenant.trial_eindigt_op && (
                        <div className="text-[11px] text-gray-500">Trial einde: {new Date(detail.tenant.trial_eindigt_op).toLocaleDateString('nl-BE')}</div>
                      )}
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded p-3">
                      <div className="text-[10px] uppercase font-bold text-gray-600 mb-1">Eigenaar</div>
                      <div className="font-bold text-base">{detail.owner?.name || '—'}</div>
                      <div className="text-xs font-mono text-gray-600 mt-1">{detail.owner?.email}</div>
                      <div className="text-[11px] text-gray-500 mt-2">Rol: <strong>{detail.owner?.role}</strong> {detail.owner?.is_super_admin && <span className="text-purple-600 font-bold">👑 SUPER</span>}</div>
                      {detail.owner?.laatst_ingelogd && (
                        <div className="text-[11px] text-gray-500">Laatste login: {new Date(detail.owner.laatst_ingelogd).toLocaleDateString('nl-BE')}</div>
                      )}
                    </div>
                  </div>

                  {/* Statistieken */}
                  <div className="grid grid-cols-4 gap-2 mb-5">
                    {[
                      { l: 'Klanten', v: detail.counts.klanten, c: '#002FA7' },
                      { l: 'Artikels', v: detail.counts.artikels, c: '#10B981' },
                      { l: 'Leveranciers', v: detail.counts.leveranciers, c: '#F59E0B' },
                      { l: 'Inkomende docs', v: detail.counts.inkomende_documenten, c: '#7C3AED' },
                    ].map(s => (
                      <div key={s.l} className="bg-white border border-gray-200 rounded p-2 text-center">
                        <div className="text-[10px] uppercase font-bold text-gray-400">{s.l}</div>
                        <div className="text-xl font-extrabold" style={{ color: s.c }}>{s.v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Doc-breakdown */}
                  {Object.keys(detail.doc_breakdown).length > 0 && (
                    <div className="mb-5">
                      <div className="text-xs font-bold uppercase text-gray-500 mb-2">Documenten per type</div>
                      <div className="bg-gray-50 rounded p-3 grid grid-cols-3 gap-2">
                        {Object.entries(detail.doc_breakdown).map(([type, v]) => (
                          <div key={type} className="text-xs">
                            <div className="font-bold capitalize">{type}</div>
                            <div className="text-gray-500">{v.aantal} st. · €{v.totaal_eur.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Verkopers binnen tenant */}
                  {detail.verkopers.length > 0 && (
                    <div className="mb-5">
                      <div className="text-xs font-bold uppercase text-gray-500 mb-2">Verkopers ({detail.verkopers.length})</div>
                      <div className="bg-gray-50 rounded p-2 space-y-1">
                        {detail.verkopers.map((v, i) => (
                          <div key={i} className="text-xs flex gap-2"><strong>{v.naam}</strong> <span className="text-gray-500">{v.email}</span></div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recente activiteit */}
                  {detail.recente_activiteit.length > 0 && (
                    <div>
                      <div className="text-xs font-bold uppercase text-gray-500 mb-2">Laatste 10 documenten</div>
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-500 text-[10px]">
                          <tr>
                            <th className="text-left px-2 py-1">Datum</th>
                            <th className="text-left px-2 py-1">Type</th>
                            <th className="text-left px-2 py-1">Nr</th>
                            <th className="text-left px-2 py-1">Klant</th>
                            <th className="text-right px-2 py-1">Totaal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.recente_activiteit.map((d, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="px-2 py-1">{d.datum ? new Date(d.datum).toLocaleDateString('nl-BE') : '—'}</td>
                              <td className="px-2 py-1 capitalize">{d.type}</td>
                              <td className="px-2 py-1 font-mono text-[11px]">{d.documentnr || '—'}</td>
                              <td className="px-2 py-1">{d.klant_naam || '—'}</td>
                              <td className="px-2 py-1 text-right font-mono">€{(d.totaal || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Audit Log Modal ─── */}
      {showAuditLog && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" onClick={() => setShowAuditLog(false)} data-testid="audit-log-modal">
          <div className="bg-white w-full max-w-3xl max-h-[80vh] overflow-y-auto rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
              <h2 className="font-bold text-lg flex items-center gap-2"><ClockCounterClockwise size={20} /> Audit log — Super-admin acties</h2>
              <button onClick={() => setShowAuditLog(false)} className="p-1 hover:bg-gray-100 rounded" data-testid="close-audit-log"><X size={18} /></button>
            </div>
            <div className="p-5">
              {auditLogs.length === 0 ? (
                <div className="text-center text-gray-400 py-8">Geen acties geregistreerd.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500 text-[10px] uppercase">
                    <tr>
                      <th className="text-left px-2 py-2">Wanneer</th>
                      <th className="text-left px-2 py-2">Wie</th>
                      <th className="text-left px-2 py-2">Tenant</th>
                      <th className="text-left px-2 py-2">Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-2 py-1.5 whitespace-nowrap">{log.at ? new Date(log.at).toLocaleString('nl-BE') : '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{log.by_email || log.by_user_id}</td>
                        <td className="px-2 py-1.5">{log.tenant_naam || log.tenant_id?.slice(0, 8) + '...'} <br /><span className="text-[10px] text-gray-500">{log.tenant_email}</span></td>
                        <td className="px-2 py-1.5">
                          <span className="inline-block px-2 py-0.5 bg-purple-50 text-purple-700 font-bold uppercase tracking-wide text-[10px] rounded">{log.actie}</span>
                          {log.extra?.extra_dagen ? <span className="ml-1 text-[10px] text-gray-500">+{log.extra.extra_dagen}d</span> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Charts component (signups bar, plan-distributie donut, MRR breakdown) ───
function SuperAdminCharts({ charts }) {
  const signups = charts?.signups_series || [];
  const plans = charts?.plan_distribution || [];
  const mrrBreak = charts?.mrr_breakdown || [];
  const totaalSignups = signups.reduce((a, b) => a + (b.count || 0), 0);
  const maxSignups = Math.max(1, ...signups.map(s => s.count || 0));
  const planKleur = { trial: '#3B82F6', starter: '#10B981', pro: '#F59E0B', enterprise: '#DC2626', lifetime: '#7C3AED', onbekend: '#94A3B8' };
  const planTotaal = plans.reduce((a, b) => a + (b.count || 0), 0);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="super-admin-charts">
      {/* Signups per dag */}
      <div className="bg-white border border-gray-200 p-4 rounded shadow-sm lg:col-span-2" data-testid="chart-signups">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900 text-sm">📈 Nieuwe registraties (30 dagen)</h3>
          <span className="text-xs text-gray-500">{totaalSignups} totaal</span>
        </div>
        <div className="flex items-end gap-px h-32" style={{ minHeight: '128px' }}>
          {signups.map((s, i) => {
            const h = s.count > 0 ? Math.max(4, (s.count / maxSignups) * 110) : 2;
            const isToday = i === signups.length - 1;
            return (
              <div key={s.date} className="flex-1 flex flex-col items-center group relative" title={`${s.date}: ${s.count}`}>
                <div
                  className={`w-full rounded-t transition-all ${isToday ? 'bg-violet-600' : 'bg-blue-500'} hover:opacity-80`}
                  style={{ height: `${h}px` }}
                />
                {s.count > 0 && (
                  <div className="absolute -top-5 opacity-0 group-hover:opacity-100 text-[10px] bg-gray-900 text-white px-1.5 py-0.5 rounded whitespace-nowrap z-10">
                    {s.date.slice(5)}: {s.count}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 mt-2">
          <span>{signups[0]?.date.slice(5)}</span>
          <span>vandaag</span>
        </div>
      </div>

      {/* Plan distribution */}
      <div className="bg-white border border-gray-200 p-4 rounded shadow-sm" data-testid="chart-plans">
        <h3 className="font-bold text-gray-900 text-sm mb-3">🎟️ Tenants per plan</h3>
        <div className="space-y-2">
          {plans.map(p => {
            const pct = planTotaal > 0 ? Math.round(p.count / planTotaal * 100) : 0;
            return (
              <div key={p.tier} className="text-xs" data-testid={`plan-row-${p.tier}`}>
                <div className="flex justify-between mb-1">
                  <span className="font-bold capitalize" style={{ color: planKleur[p.tier] || '#475569' }}>{p.tier}</span>
                  <span className="font-mono text-gray-600">{p.count} ({pct}%)</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: planKleur[p.tier] || '#475569' }} />
                </div>
              </div>
            );
          })}
          {plans.length === 0 && <div className="text-xs text-gray-400 italic">Geen data</div>}
        </div>
      </div>

      {/* MRR breakdown */}
      <div className="bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 p-4 rounded shadow-sm lg:col-span-3" data-testid="chart-mrr">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-violet-900 text-sm">💶 MRR & ARR (recurring revenue)</h3>
          <div className="text-right">
            <div className="text-xs text-violet-600 uppercase font-bold">Totaal MRR</div>
            <div className="text-2xl font-extrabold text-violet-900">€{(charts.totaal_mrr || 0).toFixed(2)}/m</div>
            <div className="text-[10px] text-violet-500">ARR: €{(charts.arr || 0).toFixed(2)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          {mrrBreak.map(m => (
            <div key={m.tier} className="bg-white/70 border border-violet-200 rounded p-2" data-testid={`mrr-tier-${m.tier}`}>
              <div className="text-[10px] uppercase font-bold text-violet-600">{m.tier}</div>
              <div className="text-lg font-extrabold text-gray-900">€{m.mrr.toFixed(0)}</div>
              <div className="text-[10px] text-gray-500">{m.actief} actief</div>
            </div>
          ))}
          {mrrBreak.length === 0 && <div className="text-xs text-gray-400 italic col-span-4">Nog geen betalende tenants</div>}
        </div>
        {/* Conversion */}
        <div className="mt-3 pt-3 border-t border-violet-200 flex items-center justify-between text-xs">
          <span className="text-violet-700">
            <strong>Conversie:</strong> {charts.conversion?.paid_ooit || 0} betaald van {charts.conversion?.trials_ooit || 0} trials
          </span>
          <span className="text-violet-900 font-bold text-base">{charts.conversion?.pct || 0}%</span>
        </div>
      </div>
    </div>
  );
}

